import { CheckCircleFilled, CloseCircleFilled, InfoCircleFilled, WarningFilled } from "@ant-design/icons";
import { TOAST_TYPES, ToastType } from "localConstants";
import React from "react";

export interface CustomToastButton {
    label: React.ReactNode;
    onClick?: () => void;
    href?: string;
    target?: string;
}

export interface CustomToastProps {
    title: React.ReactNode;
    description?: React.ReactNode;
    buttons?: CustomToastButton[];
    type?: ToastType;
}

const getButtonStyle = (type: ToastType = TOAST_TYPES.SUCCESS): React.CSSProperties => ({
    color: "#ffffff",
    backgroundColor: `var(--toastify-color-${type})` || "transparent",
});

const getIconStyle = (type: ToastType = TOAST_TYPES.SUCCESS): React.CSSProperties => ({
    fontSize: "20px",
    marginRight: "8px",
    color: `var(--toastify-color-${type})` || "transparent",
});

const getIconByType = (type: ToastType = TOAST_TYPES.SUCCESS, iconStyle: React.CSSProperties) => {
    switch (type) {
        case TOAST_TYPES.SUCCESS:
            return <CheckCircleFilled className="text-mediumGreen text-lg" style={iconStyle} />;
        case TOAST_TYPES.ERROR:
            return <CloseCircleFilled className="text-red-500 text-lg" style={iconStyle} />;
        case TOAST_TYPES.WARNING:
            return <WarningFilled className="text-yellow-500 text-lg" style={iconStyle} />;
        case TOAST_TYPES.INFO:
        default:
            return <InfoCircleFilled className="text-blue-500 text-lg" style={iconStyle} />;
    }
};

const className = `px-3 py-1 rounded font-medium text-sm transition`;

export const CustomToast: React.FC<CustomToastProps> = ({ title, description, buttons, type = TOAST_TYPES.INFO }) => {
    return (
        <div className="flex flex-col min-w-[200px] max-w-[380px]">
            <div className="flex items-center font-bold text-base mb-0.5">
                {getIconByType(type, getIconStyle(type))}
                {title}
            </div>
            {description && <div className="text-sm font-normal leading-snug break-words">{description}</div>}
            {buttons && buttons.length > 0 && (
                <div className="flex gap-2 mt-2 justify-end">
                    {buttons.map((btn, idx) => {
                        return btn.href ? (
                            <a
                                key={idx}
                                href={btn.href}
                                target={btn.target || "_self"}
                                className={className}
                                style={getButtonStyle(type)}
                                onClick={(e) => {
                                    if (btn.onClick) {
                                        e.preventDefault();
                                        btn.onClick();
                                    }
                                }}
                            >
                                {btn.label}
                            </a>
                        ) : (
                            <button key={idx} onClick={btn.onClick} className={className} style={getButtonStyle(type)}>
                                {btn.label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomToast;
